import { ChangeEvent, JSX } from 'react';
import { Controller } from 'react-hook-form';
import { ExtendedInputFieldProps } from './InputFieldProps.d';

export default function InputField(props: ExtendedInputFieldProps): JSX.Element {
  const {
    label,
    id,
    name,
    required = false,
    className = '',
    labelClass = 'labelClass',
    error,
    control,
    disabled = false,
    hideLabel = false,
  } = props;

  const inputClasses = `w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 focus:border-gray-400 dark:focus:border-gray-500 focus:outline-none dark:bg-gray-700 dark:text-white ${error ? 'border-red-500 focus:border-red-500' : ''
    } ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`;

  // Render label conditionally
  const renderLabel = () => {
    if (hideLabel) return null;

    return (
      <label htmlFor={id} className={`block text-sm font-bold text-gray-700 dark:text-gray-300 pb-1 ${labelClass}`}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    );
  };

  if (control) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <div className='flex flex-col'>
            <div className={className || ''}>
              {renderLabel()}

              {props.type === 'checkbox-group' && 'checkboxOptions' in props ? (
                <div className="flex-1">
                  <div className="mt-1 space-x-4 flex">
                    {props.checkboxOptions.map((option, index) => (
                      <div key={`${id}-${index}`} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`${id}-${index}`}
                          value={option.value}
                          checked={Array.isArray(field.value) ? field.value.includes(option.value) : false}
                          onChange={(e) => {
                            if (disabled) return; // Prevent changes if disabled
                            const updatedValue = Array.isArray(field.value) ? [...field.value] : [];
                            if (e.target.checked) {
                              updatedValue.push(option.value);
                            } else {
                              const index = updatedValue.indexOf(option.value);
                              if (index > -1) {
                                updatedValue.splice(index, 1);
                              }
                            }
                            field.onChange(updatedValue);
                          }}
                          disabled={disabled}
                          className="h-4 w-4 focus:ring-indigo-500 border-gray-300 rounded inputfield-class"
                        />
                        <label htmlFor={`${id}-${index}`} className={`ml-2 text-sm ${disabled ? 'text-gray-500' : ''}`}>
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ) : props.type === 'checkbox' && 'checkboxLabel' in props ? (
                <div className="flex-1 mt-1">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={id}
                      checked={field.value || false}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={disabled}
                      className="h-4 w-4 focus:ring-indigo-500 border-gray-300 rounded inputfield-class"
                    />
                    {props.checkboxLabel && (
                      <label htmlFor={id} className={`ml-2 text-sm ${disabled ? 'text-gray-500' : ''}`}>
                        {props.checkboxLabel}
                      </label>
                    )}
                  </div>
                </div>
              ) : props.type === 'radio' && 'radioOptions' in props ? (
                <div className="flex-1">
                  <div className="mt-1 space-x-4 flex">
                    {props.radioOptions.map((option) => (
                      <div key={`${id}-${option.value}`} className="flex items-center">
                        <input
                          type="radio"
                          id={`${id}-${option.value}`}
                          {...field}
                          value={option.value}
                          checked={field.value === option.value}
                          disabled={disabled}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 inputfield-class"
                        />
                        <label
                          htmlFor={`${id}-${option.value}`}
                          className={`ml-2 text-sm text-gray-700 dark:text-gray-300 ${disabled ? 'text-gray-500' : ''}`}
                        >
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ) : props.type === 'select' && 'options' in props ? (
                <select
                  {...field}
                  id={id}
                  disabled={disabled}
                  className={`${inputClasses} inputfield-class`}
                >
                  {props.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : props.type === 'textarea' ? (
                <textarea
                  {...field}
                  id={id}
                  placeholder={props.placeholder}
                  maxLength={props.maxLength}
                  disabled={disabled}
                  rows={'rows' in props ? props.rows : 4}
                  className={`${inputClasses} inputfield-class resize-y`}
                  autoComplete="off"
                />
              ) : (
                <input
                  {...field}
                  type={props.type || 'text'}
                  id={id}
                  placeholder={props.placeholder}
                  maxLength={props.maxLength}
                  disabled={disabled}
                  className={`${inputClasses} inputfield-class`}
                  {...(props.type === 'number' && {
                    min: props.min,
                    max: props.max,
                  })}
                  autoComplete="off"
                />
              )}
            </div>
            {error && <p className="my-1 text-xs text-red-600">{error}</p>}
          </div>
        )}
      />
    );
  }

  // Uncontrolled version
  return (
    <div className='flex flex-col'>
      <div className={className}>
        {renderLabel()}

        {props.type === 'checkbox-group' && 'checkboxOptions' in props ? (
          <div className="flex-1">
            <div className="flex gap-6 items-center">
              {props.checkboxOptions.map((option, index) => (
                <div key={`${id}-${index}`} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`${id}-${index}`}
                    name={`${name}[${index}]`}
                    value={option.value}
                    checked={Array.isArray(props.value) ? props.value.includes(option.value) : false}
                    onChange={props.onChange as (e: ChangeEvent<HTMLInputElement>) => void}
                    disabled={disabled}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded inputfield-class"
                    
                  />
                  <label htmlFor={`${id}-${index}`} className={`ml-2 text-sm text-gray-600 ${disabled ? 'text-gray-500' : ''}`}>
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ) : props.type === 'checkbox' && 'checkboxLabel' in props ? (
          <div className="flex-1 mt-1">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={id}
                name={name}
                checked={props.value as boolean || false}
                onChange={props.onChange as (e: ChangeEvent<HTMLInputElement>) => void}
                disabled={disabled}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded inputfield-class"
              />
              {props.checkboxLabel && (
                <label htmlFor={id} className={`ml-2 text-sm text-gray-600 ${disabled ? 'text-gray-500' : ''}`}>
                  {props.checkboxLabel}
                </label>
              )}
            </div>
          </div>
        ) : props.type === 'radio' && 'radioOptions' in props ? (
          <div className="flex-1">
            <div className="mt-1 space-x-4 flex">
              {props.radioOptions.map((option) => (
                <div key={`${id}-${option.value}`} className="flex items-center">
                  <input
                    type="radio"
                    id={`${id}-${option.value}`}
                    name={name}
                    value={option.value}
                    checked={props.value === option.value}
                    onChange={props.onChange as (e: ChangeEvent<HTMLInputElement>) => void}
                    disabled={disabled}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 inputfield-class"
                  />
                  <label
                    htmlFor={`${id}-${option.value}`}
                    className={`ml-2 text-sm text-gray-700 dark:text-gray-300 ${disabled ? 'text-gray-500' : ''}`}
                  >
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        ) : props.type === 'select' && 'options' in props ? (
          <select
            id={id}
            name={name}
            value={props.value as string}
            onChange={props.onChange as (e: ChangeEvent<HTMLSelectElement>) => void}
            onBlur={props.onBlur}
            required={required}
            disabled={disabled}
            className={`${inputClasses} flex-1 inputfield-class`}
            defaultValue={props.defaultValue}
          >
            {props.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : props.type === 'textarea' ? (
          <textarea
            id={id}
            name={name}
            value={props.value as string}
            onChange={props.onChange as (e: ChangeEvent<HTMLTextAreaElement>) => void}
            onBlur={props.onBlur}
            required={required}
            placeholder={props.placeholder}
            maxLength={props.maxLength}
            disabled={disabled}
            rows={'rows' in props ? props.rows : 4}
            className={`${inputClasses} flex-1 inputfield-class resize-y`}
            defaultValue={props.defaultValue as string}
            autoComplete="off"
          />
        ) : (
          <input
            type={props.type || 'text'}
            id={id}
            name={name}
            value={props.value as string}
            onChange={props.onChange as (e: ChangeEvent<HTMLInputElement>) => void}
            onBlur={props.onBlur}
            required={required}
            placeholder={props.placeholder}
            maxLength={props.maxLength}
            disabled={disabled}
            className={`${inputClasses} flex-1 inputfield-class`}
            defaultValue={props.defaultValue as string}
            {...(props.type === 'number' && {
              min: props.min,
              max: props.max,
            })}
            autoComplete="off"
          />
        )}
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}